import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { GPSProvider, useGPS } from '../../contexts/GPSContext';
import usePushNotifications from '../../hooks/usePushNotifications';
import PermissionGate from '../../components/PermissionGate';
import ProfilePhoto from '../../components/ProfilePhoto';
import VersionPill from '../../components/VersionPill';
import api from '../../lib/api';
import { useDeepTranslate } from '../../lib/useDeepTranslate';
import { House, Path, GasPump, SignOut, Bell, BellRinging, Globe, NavigationArrow, MapPin, Pencil } from '@phosphor-icons/react';

const NAV = [
  { path: '/pilot', label: 'Home', tkey: 'check_in', icon: House, end: true },
  { path: '/pilot/trips', label: 'Trips', tkey: 'my_trips', icon: Path },
  { path: '/pilot/route', label: 'Route', tkey: 'route', icon: NavigationArrow },
  { path: '/pilot/fuel', label: 'Fuel', tkey: 'fuel_records', icon: GasPump },
];

const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'ta', label: 'தமிழ்' },
  // Phase 78ab — "Complete Tamil" extends standard Tamil by translating
  // every dynamic field (supplier/item/booker/halt/remark) via the
  // backend Claude endpoint, then caching results so each unique string
  // is only translated once.
  { code: 'ta_full', label: 'தமிழ் (முழுமை)', isDeep: true },
];

export default function PilotLayout() {
  return (
    <PermissionGate>
      <GPSProvider>
        <PilotLayoutInner />
      </GPSProvider>
    </PermissionGate>
  );
}

function PilotLayoutInner() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const tr = useDeepTranslate();
  const [showLang, setShowLang] = useState(false);
  const [vehicleInfo, setVehicleInfo] = useState(null);
  const push = usePushNotifications();
  const gps = useGPS();

  // Fetch pilot's assigned vehicle
  useEffect(() => {
    api.get('/pilot/dashboard').then(res => {
      if (res.data?.vehicle) setVehicleInfo(res.data.vehicle);
    }).catch(() => {});
  }, []);

  // Auto-subscribe to push on first load if permission already granted
  useEffect(() => {
    if (push.supported && push.permission === 'granted' && !push.subscribed) {
      push.subscribe();
    }
  }, [push.supported, push.permission, push.subscribed]); // eslint-disable-line react-hooks/exhaustive-deps

  // Phase 76g — Always start GPS the moment a pilot session is alive,
  // regardless of check-in/check-out state. This ensures Live Tracking on
  // the admin map gets fresh fixes even when:
  //   - Pilot opens the app but hasn't checked in yet (fresh APK install)
  //   - Pilot is in transit between trips (technically off-shift)
  //   - Pilot's check-in row failed to persist for any reason
  // Backend `_can_auto_allocate` still gates allocation on check-in; this
  // only fixes the visibility gap.
  //
  // Phase 76l (Feb-2026) — added `gps.tracking` to the dep array so this
  // effect re-fires the moment tracking is dropped (mobile OS memory
  // reclaim, SW crash, tab backgrounded too long, React state reset). The
  // 5 s timer is a debounce: if GPS truly is running we won't fire start();
  // if it's stopped we restart it without bombing the GPS pipe with calls.
  useEffect(() => {
    if (!user || gps.tracking) return undefined;
    const t = setTimeout(() => {
      if (!gps.tracking) gps.start();
    }, 5000);
    return () => clearTimeout(t);
  }, [user, gps.tracking]); // eslint-disable-line react-hooks/exhaustive-deps

  const changeLang = (code) => {
    // Phase 78ab — `ta_full` is the "Complete Tamil" deep-translate mode.
    // Backbone i18n still runs in `ta` for menu labels; we additionally
    // set a `carloo_deep_tr` flag so the useDeepTranslate hook can opt in.
    if (code === 'ta_full') {
      i18n.changeLanguage('ta');
      localStorage.setItem('carloo_lang', 'ta_full');
      localStorage.setItem('carloo_deep_tr', '1');
    } else {
      i18n.changeLanguage(code);
      localStorage.setItem('carloo_lang', code);
      localStorage.removeItem('carloo_deep_tr');
    }
    // Notify any mounted hooks to re-translate immediately.
    window.dispatchEvent(new Event('carloo-lang-changed'));
    setShowLang(false);
  };

  return (
    <div className="flex flex-col min-h-screen bg-[#F7F7F7]" data-testid="pilot-layout">
      {/* Top Header */}
      <header className="bg-[#0A0A0A] text-white px-3 py-2.5 flex items-center justify-between sticky top-0 z-40">
        <div className="flex items-center gap-2.5 min-w-0">
          <ProfilePhoto currentPhoto={user?.profile_photo} onPhotoUpdate={() => {}} size="sm" />
          <div className="min-w-0">
            <img src="/carloo-logo.png" alt="Carloo" className="h-6 invert" />
            <p className="text-white/40 text-[11px] truncate">{user?.name || t('pilot')}</p>
            {vehicleInfo && (
              <p className="text-[#00C853] text-[10px] font-mono truncate">{vehicleInfo.vehicle_number} {vehicleInfo.vehicle_id_manual ? `(${vehicleInfo.vehicle_id_manual})` : ''}</p>
            )}
            {/* Phase 76u — version pill on dark header. Tiny so it doesn't
                fight with the vehicle number. */}
            <VersionPill className="!bg-white/10 !text-white/70 mt-0.5 !text-[9px] !py-0.5" />
          </div>
        </div>
        <div className="flex items-center gap-1">
          {/* GPS status indicator — Phase 76g: always visible so pilots can see
              whether tracking is alive. Red OFF dot means admin can't see them. */}
          <div
            data-testid="gps-status-icon"
            title={
              gps.tracking
                ? `GPS Active${gps.lastPos ? ` · ${gps.lastPos.speed} km/h · ${gps.pointCount} pts` : ''}${gps.queuedCount > 0 ? ` · ${gps.queuedCount} queued` : ''}${gps.error ? ` · ${gps.error}` : ''}`
                : (gps.error ? `GPS error: ${gps.error}. Tap to retry.` : 'GPS off. Tap to start.')
            }
            onClick={() => {
              if (!gps.tracking) gps.start();
            }}
            className="relative p-2 cursor-pointer"
          >
            <NavigationArrow
              size={18}
              weight="fill"
              className={
                !gps.tracking
                  ? 'text-[#FF3333]'
                  : (gps.error ? 'text-[#FF3333]' : (gps.queuedCount > 0 ? 'text-[#FF8C00]' : 'text-[#00C853]'))
              }
            />
            {gps.tracking && gps.queuedCount > 0 && (
              <span
                data-testid="gps-queued-badge"
                className={`absolute -top-0.5 -right-0.5 text-[8px] font-bold leading-none px-1 py-0.5 rounded-full ${gps.unauthorized ? 'bg-[#FF3333] text-white' : 'bg-[#FF8C00] text-white'}`}
              >
                {gps.queuedCount > 99 ? '99+' : gps.queuedCount}
              </span>
            )}
            {!gps.tracking && (
              <span className="absolute -top-0.5 -right-0.5 text-[8px] font-bold leading-none px-1 py-0.5 rounded-full bg-[#FF3333] text-white" data-testid="gps-off-badge">OFF</span>
            )}
          </div>

          {/* Language Selector */}
          <div className="relative">
            <button
              data-testid="language-selector-btn"
              onClick={() => setShowLang(!showLang)}
              className="p-2 text-white/60 hover:text-white flex items-center gap-1"
            >
              <Globe size={18} />
              <span className="text-xs uppercase">
                {(localStorage.getItem('carloo_lang') || i18n.language || 'en') === 'ta_full'
                  ? 'TA+'
                  : (i18n.language || 'en').slice(0, 2)}
              </span>
            </button>
            {showLang && (
              <div className="absolute right-0 top-full mt-1 bg-white border border-[#E5E5E5] shadow-lg z-50 min-w-[180px]">
                {LANGUAGES.map(lang => {
                  const current = localStorage.getItem('carloo_lang') || i18n.language || 'en';
                  const active = current === lang.code;
                  return (
                    <button
                      key={lang.code}
                      data-testid={`lang-${lang.code}`}
                      onClick={() => changeLang(lang.code)}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-[#F3F3F3] flex items-center justify-between gap-2 ${active ? 'bg-[#002FA7]/10 text-[#002FA7] font-medium' : 'text-[#0A0A0A]'}`}
                    >
                      <span>{lang.label}</span>
                      {lang.isDeep && (
                        <span className="text-[9px] uppercase tracking-widest bg-[#A855F7]/10 text-[#A855F7] px-1.5 py-0.5 rounded">AI</span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <button
            className="relative p-2"
            data-testid="pilot-notifications"
            onClick={() => {
              if (!push.subscribed && push.supported) push.subscribe();
            }}
            title={push.subscribed ? 'Notifications enabled' : 'Enable notifications'}
          >
            {push.subscribed ? (
              <BellRinging size={20} className="text-[#00C853]" weight="fill" />
            ) : (
              <Bell size={20} className="text-white/60" />
            )}
            {!push.subscribed && push.supported && (
              <span className="absolute top-0.5 right-0.5 w-2.5 h-2.5 bg-[#FF3333] rounded-full border-2 border-[#0A0A0A]" />
            )}
          </button>
          <button
            data-testid="pilot-logout-button"
            onClick={async () => { await logout(); navigate('/login'); }}
            className="p-2 text-white/50 hover:text-[#FF3333]"
          >
            <SignOut size={20} />
          </button>
        </div>
      </header>

      {/* Halt strip */}
      <div className="bg-white border-b border-[#E5E5E5] px-4 py-1.5 flex items-center gap-2 text-xs">
        <MapPin size={13} className="text-[#002FA7] flex-shrink-0" />
        <span className="text-[#666] truncate">
          Halt: <strong className="text-[#0A0A0A]">{tr(user?.halt_location) || 'Not set'}</strong>
        </span>
        <button
          type="button"
          onClick={() => window.dispatchEvent(new CustomEvent('open-halt-dialog'))}
          className="ml-auto text-[10px] uppercase tracking-widest text-[#002FA7] hover:text-[#002FA7]/70 underline underline-offset-2 flex items-center gap-1"
          data-testid="change-halt-btn"
        >
          <Pencil size={10} weight="bold" /> Change
        </button>
      </div>

      {/* Content. `pb-20` reserves space for the bottom nav; the extra
          `safe-area-inset-bottom` padding on the nav itself adds the
          Android gesture/3-button bar height so the two never overlap.
          The `max()` guarantees at least 8px gap even when safe-area
          returns 0 (older Android, web preview, narrow PWA install). */}
      <main className="flex-1 p-4" style={{ paddingBottom: 'calc(5rem + env(safe-area-inset-bottom, 0px))' }}>
        <Outlet />
      </main>

      {/* Phase 76l — Pool offer modal removed. Pooled trips now silently
          auto-attach to the closest pilot via pool_engine.start_pool_offers
          (no accept/reject UI). */}

      {/* Bottom Navigation. Phase 76r (Feb-2026) — pad the inner content
          by `env(safe-area-inset-bottom)` so the nav buttons sit ABOVE the
          Android system navigation bar (3-button or gesture) on phones
          with edge-to-edge displays (targetSdk=35 forces this). The
          `max()` fallback guarantees an 8px gap even when safe-area
          isn't populated by the WebView. */}
      <nav
        className="fixed bottom-0 inset-x-0 z-50 backdrop-blur-xl bg-white/80 border-t border-[#E5E5E5] flex"
        style={{ paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 8px)' }}
      >
        {NAV.map(item => (
          <NavLink
            key={item.path}
            to={item.path}
            end={item.end}
            className={({ isActive }) =>
              `flex-1 flex flex-col items-center gap-1 py-3 min-h-[48px] transition-all duration-150 ${
                isActive ? 'text-[#002FA7]' : 'text-[#666]'
              }`
            }
          >
            <item.icon size={22} weight="regular" />
            <span className="text-xs font-medium">{item.label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
